import { notification } from 'antd';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';
import { FilesTableEnum } from '../enums/FilesTableEnum';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { UserRoleEnum } from '../enums/UserRoleEnum';
import { useModal } from '../hooks/useModal';
import type AuthenticatedUser from '../interfaces/AuthenticatedUser';
import type DeviceInfo from '../interfaces/DeviceInfo';
import type User from '../interfaces/User';
import EventBus from '../services/EventBus';
import { UserService } from '../services/userService';
import { useAppStore } from '../store/store';
import { clearAllCookies } from '../utils/cookie';
import { generateDeviceInfo } from '../utils/deviceInfo';

import LoadingOverlay from '../components/Common/LoadingOverlay';
import { keycloakService } from '../lib/keycloak';
import { clearStorage, getStorageItem, storeStorageItem } from '../utils/storage';

interface AuthContextType {
  isAdmin: boolean;
  isLoggedIn: boolean;
  isContributor: boolean;
  isExternal: boolean;
  isAdminOrContributor: boolean;
  isReader: boolean;
  token: string | null;
  authenticatedUser: AuthenticatedUser | null;
  logout: () => void;
  redirectLoggedInUser: () => void;
  login: (token: string, redirectHome: boolean) => Promise<void>;
  onBehalfOfUser: User | null;
  setOnBehalfOfUser: (user: User) => void;
  isOnBehalfOfNullOrAdmin: boolean;
  deviceInfo: DeviceInfo | null;
  isOnBehalfOfUser: boolean;
  handleOnBehalfOfUserChange: (user: User | null) => void;
  isInFileRoute: (route: FilesTableEnum) => boolean;
  onBehalfOfUserChanged: (callback: (user: User | null) => void) => void;
  offBehalfOfUserChanged: (callback: (user: User | null) => void) => void;
  handleSocialLogin: (type?: string) => void;
}

export const AuthContext = createContext<AuthContextType>({
  deviceInfo: null,
  isAdmin: false,
  isAdminOrContributor: false,
  isContributor: false,
  isExternal: false,
  isLoggedIn: false,
  isOnBehalfOfNullOrAdmin: false,
  isReader: false,
  login: async () => {},
  logout: () => {},
  onBehalfOfUser: null,
  redirectLoggedInUser: () => {},
  setOnBehalfOfUser: () => {},
  token: null,
  authenticatedUser: null,
  isOnBehalfOfUser: false,
  handleOnBehalfOfUserChange: () => {},
  isInFileRoute: () => false,
  onBehalfOfUserChanged: () => {},
  offBehalfOfUserChanged: () => {},
  handleSocialLogin: () => {},
});

const userService = new UserService();
const deviceInfo = generateDeviceInfo();

interface AuthProviderProps {
  children: ReactNode;
}

const isKCEnabled = import.meta.env.VITE_APP_KEYCLOAK_AUTH_ENABLED === 'true';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notificationApi, notificationContextHolder] = notification.useNotification();

  const { t } = useTranslation();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [onBehalfOfUser, setOnBehalfOfUser] = useState<User | null>(null);
  const [isLoadingAuthenticatedUser, setIsLoadingAuthenticatedUser] = useState<boolean>(true);
  const onBehalfOfUserChangedSubscribers = useRef(new Set<(user: User | null) => void>());
  const modal = useModal();
  const brandingData = useAppStore((s) => s.brandingData);

  const [kcAuthenticated, setKcAuthenticated] = useState<boolean | null>(null);
  const bootIdRef = useRef(0);

  /**
   * Logs the user out of the application by performing the following actions:
   * - Clears the current user information from the application state.
   * - Resets the authentication token.
   * - Removes all stored cookies related to the session.
   * - Clears other stored data.
   * - Redirects the user to the login page.
   *
   * This function ensures that the user's session is fully terminated and they
   * are safely navigated back to the login screen.
   */
  const logout = (): void => {
    if (kcAuthenticated) {
      const kc = keycloakService.getKeycloak();
      if (kc?.authenticated) {
        keycloakService.logout();
      }
    }

    setAuthenticatedUser(null);
    setToken(null);
    clearAllCookies();
    clearStorage();
    if (isKCEnabled && brandingData?.realm) {
      keycloakService.setRealm(brandingData.realm);
    }
    navigate('/login');
  };

  /**
   * Handles the social login process based on the provided type.
   *
   * @returns {void}
   */
  const handleSocialLogin = (type?: string): void => {
    if (type === 'cybergrant') {
      keycloakService.login();
    } else {
      keycloakService.login(type);
    }
  };

  /**
   * Asynchronously logs in a user by storing their token, updating the app's state,
   * and optionally redirecting to a home page based on the user's role.
   *
   * @param {User} user - The user object containing authentication and role information.
   * @param {boolean} [redirectHome=false] - Indicates whether the user should be redirected to the home page after login.
   * @returns {Promise<void>}
   */
  const login = async (token: string, redirectHome: boolean = false): Promise<void> => {
    await storeStorageItem('localStorage', StorageItemKeyEnum.Token, token);
    setToken(token || null);

    await userService
      .getUserInfo()
      .then(async (user) => {
        setAuthenticatedUser(user);

        if (redirectHome) {
          switch (user.role) {
            case UserRoleEnum.Reader:
            case UserRoleEnum.External:
              navigate('/files/received', { replace: true });
              break;
          }
        }
      })
      .catch(() => {
        notificationApi.error({
          message: t('errors.genericFetchError', { resource: t('common.user') }),
        });
        logout();
      });
  };

  /**
   * Registers a callback function to be called when the "on behalf of user" state changes.
   *
   * @param {function} callback - The callback function to be called when the "on behalf of user" state changes. This function should accept a user object of type User or null.
   */
  const onBehalfOfUserChanged = (callback: (user: User | null) => void) => {
    onBehalfOfUserChangedSubscribers.current.add(callback);
  };

  /**
   * Removes a previously registered callback for the "on behalf of user" state change.
   *
   * @param {function} callback - The callback function to be removed. This function should accept a user object of type User or null.
   */
  const offBehalfOfUserChanged = (callback: (user: User | null) => void) => {
    onBehalfOfUserChangedSubscribers.current.delete(callback);
  };

  /**
   * A callback function to handle changes to the "on behalf of user" state.
   * Updates the state with the provided user object or null if no user is provided.
   *
   * @param {User | null} user - The user object to set as the "on behalf of user" state, or null to reset it.
   * @returns {void}
   */
  const handleOnBehalfOfUserChange = useCallback((user: User | null) => {
    if (user) {
      modal
        .open({
          title: t('modal.enterOnBehalfOf.title'),
          message: t('modal.enterOnBehalfOf.description', {
            resource: `${user.firstName} ${user.lastName}`,
          }),
        })
        .then(() => {
          setOnBehalfOfUser(user);
          onBehalfOfUserChangedSubscribers.current.forEach((callback) => callback(user));
        });
    } else {
      setOnBehalfOfUser(null);
      onBehalfOfUserChangedSubscribers.current.forEach((callback) => callback(null));
    }
  }, []);

  /**
   * Redirects a logged-in user to a specific route based on their role.
   *
   * This function checks if the user is logged in and if the application user data is available.
   * Depending on the role of the `appUser`, it navigates the user to a different endpoint:
   * - For roles `Reader` and `External`, the user is redirected to `/files?list=0&table=0&type=1`.
   * - For any other roles, the user is redirected to `/files?list=0&table=0&type=2`.
   *
   * The navigation replaces the current history entry to prevent the user from navigating back to the previous screen.
   *
   * Preconditions:
   * - `isLoggedIn` should be a boolean indicating if the user is logged in.
   * - `appUser` should contain user data, including the `role` property.
   * - `navigate` should be a function capable of handling navigation.
   * - `UserRoleEnum` should be an enumeration of possible user roles.
   */
  const redirectLoggedInUser = () => {
    if (isLoggedIn && authenticatedUser) {
      switch (authenticatedUser.role) {
        case UserRoleEnum.Reader:
        case UserRoleEnum.External:
          navigate(`/files/${FilesTableEnum.Received}`, { replace: true });
          break;
        default:
          navigate(`/files/${FilesTableEnum.Personal}`, { replace: true });
          break;
      }
    }
  };

  /**
   * Determines if the current location pathname includes a specific file route.
   *
   * @param {FilesTableEnum} route - The file route to check for in the current pathname.
   * @returns {boolean} True if the pathname includes the specified route, false otherwise.
   */
  const isInFileRoute = useCallback(
    (route: FilesTableEnum) => {
      return location.pathname.includes(route);
    },
    [location.pathname],
  );

  /**
   * Determines if the user is logged in based on the presence of a token.
   *
   * @returns {boolean} True if the user is logged in, false otherwise.
   */
  const isLoggedIn = useMemo(() => !!token, [token]);

  /**
   * Determines if the user is an admin based on their role.
   *
   * @returns {boolean} True if the user is an admin, false otherwise.
   */
  const isAdmin = useMemo(
    () => authenticatedUser?.role === UserRoleEnum.Admin,
    [authenticatedUser],
  );

  /**
   * Determines if the user is a contributor based on their role.
   *
   * @returns {boolean} True if the user is a contributor, false otherwise.
   */
  const isContributor = useMemo(
    () => authenticatedUser?.role === UserRoleEnum.Contributor,
    [authenticatedUser],
  );

  /**
   * Determines if the user is a reader based on their role.
   *
   * @returns {boolean} True if the user is a reader, false otherwise.
   */
  const isReader = useMemo(
    () => authenticatedUser?.role === UserRoleEnum.Reader,
    [authenticatedUser],
  );

  /**
   * Determines if the user is an external user based on their role.
   *
   * @returns {boolean} True if the user is an external user, false otherwise.
   */
  const isExternal = useMemo(
    () => authenticatedUser?.role === UserRoleEnum.External,
    [authenticatedUser],
  );

  /**
   * Determines if the user is an admin or a contributor based on their role.
   *
   * @returns {boolean} True if the user is an admin or a contributor, false otherwise.
   */
  const isAdminOrContributor = useMemo(() => isAdmin || isContributor, [isAdmin, isContributor]);

  /**
   * Determines if the user is on behalf of a null or admin user.
   *
   * @returns {boolean} True if the user is on behalf of a null or admin user, false otherwise.
   */
  const isOnBehalfOfNullOrAdmin = useMemo(
    () => isAdmin || !onBehalfOfUser,
    [isAdmin, onBehalfOfUser],
  );

  /**
   * Determines if the user is on behalf of a user.
   *
   * @returns {boolean} True if the user is on behalf of a user, false otherwise.
   */
  const isOnBehalfOfUserFlag = useMemo(() => !!onBehalfOfUser, [onBehalfOfUser]);

  /**
   * Context value
   */
  const contextValue = useMemo(() => {
    return {
      deviceInfo,
      isAdmin,
      isAdminOrContributor,
      isContributor,
      isExternal,
      isLoggedIn,
      isOnBehalfOfNullOrAdmin,
      isReader,
      login,
      logout,
      onBehalfOfUser,
      redirectLoggedInUser,
      setOnBehalfOfUser,
      token,
      authenticatedUser,
      isOnBehalfOfUser: isOnBehalfOfUserFlag,
      handleOnBehalfOfUserChange,
      isInFileRoute,
      onBehalfOfUserChanged,
      offBehalfOfUserChanged,
      handleSocialLogin,
    };
  }, [
    deviceInfo,
    isAdmin,
    isAdminOrContributor,
    isContributor,
    isExternal,
    isLoggedIn,
    isOnBehalfOfNullOrAdmin,
    isReader,
    login,
    logout,
    onBehalfOfUser,
    redirectLoggedInUser,
    setOnBehalfOfUser,
    token,
    authenticatedUser,
    isOnBehalfOfUserFlag,
    handleOnBehalfOfUserChange,
    isInFileRoute,
    onBehalfOfUserChanged,
    offBehalfOfUserChanged,
    handleSocialLogin,
  ]);

  useEffect(() => {
    (async () => {
      const storedOnBehalfOfUser = await getStorageItem(
        'sessionStorage',
        StorageItemKeyEnum.onBehalfOfUser,
      );
      if (storedOnBehalfOfUser) {
        setOnBehalfOfUser(storedOnBehalfOfUser);
      }
    })();
  }, []);

  useEffect(() => {
    storeStorageItem('sessionStorage', StorageItemKeyEnum.onBehalfOfUser, onBehalfOfUser);
  }, [onBehalfOfUser]);

  useEffect(() => {
    if (!brandingData?.realm || authenticatedUser?.id) {
      setIsLoadingAuthenticatedUser(false);
      return;
    }

    let unsubTokenChange: (() => void) | undefined;
    let cancelled = false;
    const myBootId = ++bootIdRef.current;

    async function doAuthBoot() {
      setIsLoadingAuthenticatedUser(true);

      const storedToken = await getStorageItem('localStorage', StorageItemKeyEnum.Token);

      try {
        if (cancelled || bootIdRef.current !== myBootId) return; // Stop se effect già superato

        const hasEnabledProvider = Object.values(brandingData?.providers ?? {}).some(
          (provider) => provider.isEnabled,
        );

        if (storedToken) {
          const user = await userService.getUserInfo();
          if (cancelled || bootIdRef.current !== myBootId) return;
          setAuthenticatedUser(user);
          setToken(storedToken || null);
        } else if (isKCEnabled && hasEnabledProvider) {
          // Keycloak
          if (!keycloakService.getRealm() && brandingData?.realm) {
            keycloakService.setRealm(brandingData.realm);
          }
          await keycloakService.init();
          setToken(keycloakService.getToken());

          if (keycloakService.isAuthenticated()) {
            setKcAuthenticated(true);
            const user = await userService.getUserInfo();
            if (cancelled || bootIdRef.current !== myBootId) return;
            setAuthenticatedUser(user);

            // Ascolta cambi token
            const onChange = (t: string | null) => {
              if (t) setToken(t);
              else keycloakService.logout();
            };
            keycloakService.onTokenChange(onChange);
            unsubTokenChange = () => keycloakService.offTokenChange(onChange);
          }
        }
      } catch (err) {
        console.error('Error during setup:', err);
        notification.error({
          message:
            t?.('errors.genericFetchError', { resource: t?.('common.user') }) ?? 'Auth error',
        });
        logout();
      } finally {
        if (!cancelled && bootIdRef.current === myBootId) {
          setIsLoadingAuthenticatedUser(false);
        }
      }
    }

    doAuthBoot();

    return () => {
      cancelled = true;
      unsubTokenChange?.();
    };
  }, [brandingData?.realm, authenticatedUser?.id, isKCEnabled]);

  useEffect(() => {
    const handler = () => {
      logout();
    };
    EventBus.on(EventBusEventEnum.MakeLogout, handler);
    return () => {
      EventBus.off(EventBusEventEnum.MakeLogout, handler);
    };
  }, []);

  return (
    <AuthContext.Provider value={contextValue}>
      <LoadingOverlay label={t('common.loading')} isVisible={isLoadingAuthenticatedUser} />

      {!isLoadingAuthenticatedUser && (
        <>
          {notificationContextHolder}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};
